import {Popover} from '@dropbox/dig-components/popover';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text, type TextProps} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AiLine,
  CheckmarkCircleFill,
  CheckmarkLine,
  CloseLine,
  FailFill,
  InfoLine,
  SyncingLine,
} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {derivedThemeAtom} from 'atoms/layout';
import {
  AiWorkstreamHealthScoreService,
  type Workstream,
  type WorkstreamHealthScoreResponse,
} from 'client';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getService} from 'utilities';
import {queryClient} from 'views/QueryClientWrapper';

import {
  calculateHealthScore,
  getHealthScoreCriteria,
  getLocalHealthScoreChecksToDisplay,
  getServerHealthScoreChecksToDisplay,
  getWorkstreamCheckDescription,
  getWorkstreamCheckSource,
  isValidWorkstreamHealthScore,
  useEnabledGrowthbookFeatureIds,
  useWorkstreamHealthScore,
  type WorkstreamCheckResponseWithSource,
  type WorkstreamCheckType,
} from './workstreamChecks';
import styles from './WorkstreamHealthScore.module.css';

export const HEALTH_SCORE_COMPLETE_COLOR = '#206F54';
export const HEALTH_SCORE_CRITICAL_COLOR = '#C0154C';
const DARK_MODE_MUTED_BAR_OPACITY = 0.5;
// About "Needs Attention", the longest score label, so the row barely shifts.
const SKELETON_TEXT_WIDTH_PX = 96;

const getWorkstreamHealthScorePendingQueryKey = (
  workstreamId: number,
  checksType: 'default' | 'oceo'
) => ['workstream', 'health_score', 'pending', workstreamId, checksType];

type PendingWorkstreamHealthScoreTask = {taskId: string; skipAIChecks: boolean};

const usePendingWorkstreamHealthScoreTask = ({
  workstreamId,
  checksType,
}: {
  workstreamId: number;
  checksType: 'default' | 'oceo';
}) => {
  const {data} = useQuery({
    queryKey: getWorkstreamHealthScorePendingQueryKey(workstreamId, checksType),
    queryFn: () =>
      queryClient.getQueryData<PendingWorkstreamHealthScoreTask | null>(
        getWorkstreamHealthScorePendingQueryKey(workstreamId, checksType)
      ) ?? null,
    initialData: () =>
      queryClient.getQueryData<PendingWorkstreamHealthScoreTask | null>(
        getWorkstreamHealthScorePendingQueryKey(workstreamId, checksType)
      ) ?? null,
    enabled: workstreamId > 0,
    staleTime: Infinity,
  });

  return data ?? undefined;
};

export const useWorkstreamHealthScores = ({
  checksType,
  timeframe,
}: {
  checksType: 'default' | 'oceo';
  timeframe: string | undefined;
}) => {
  const enabledGrowthbookFeatureIds = useEnabledGrowthbookFeatureIds();
  const {data, isLoading} = useQuery({
    queryKey: ['workstream', 'health_scores', checksType, timeframe],
    queryFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).getWorkstreamHealthScoresApiV1WorkstreamHealthScoresGet(checksType, timeframe),
    retry: false,
  });
  const healthScores: WorkstreamHealthScoreResponse[] = [
    ...(data?.workstream_health_scores ?? []),
  ].map((healthScore) => {
    if (isValidWorkstreamHealthScore(healthScore, enabledGrowthbookFeatureIds)) {
      return healthScore;
    }

    return {
      task_id: '',
      workstream: healthScore.workstream,
      workstream_pillar_metadata: healthScore.workstream_pillar_metadata,
    };
  });
  healthScores.sort((a, b) => {
    const aHasScore = !!a.task_id;
    const bHasScore = !!b.task_id;
    if (!aHasScore && bHasScore) return 1;
    if (aHasScore && !bHasScore) return -1;
    return (a.workstream?.name ?? '').localeCompare(b.workstream?.name ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
  return {healthScores, isLoading};
};

export const useWorkstreamHealthScoreContext = (
  checksType: 'default' | 'oceo',
  workstreamId?: number,
  timeframe?: string
) => {
  const {data, isLoading, error} = useQuery({
    queryKey: ['workstream', 'health_score', 'context', workstreamId, checksType, timeframe],
    queryFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).checkWorkstreamHealthScoreContextApiV1WorkstreamIdHealthScoreContextGet(
        workstreamId!,
        checksType,
        timeframe
      ),
    enabled: !!workstreamId,
    retry: false,
  });
  return {context: data?.context, isLoading, error};
};

// Hook for Health Score generation with task ID polling pattern
export const useWorkstreamHealthScoreGenerate = ({
  workstreamId,
  checksType = 'default',
  timeframe,
  onComplete,
}: {
  workstreamId: number;
  checksType: 'default' | 'oceo';
  timeframe: string | undefined;
  onComplete?: () => void;
}) => {
  if (!workstreamId) {
    throw new Error('Workstream ID must be provided');
  }

  const [generateTaskId, setGenerateTaskId] = useState<string | undefined>(undefined);

  // Mutation to kick off generation
  const {mutateAsync: generateApi} = useMutation({
    mutationFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).generateWorkstreamHealthScoreApiV1WorkstreamHealthScoreGeneratePost({
        workstream_id: workstreamId,
        checks_type: checksType,
        timeframe: timeframe,
      }),
  });

  const isGenerating = !!generateTaskId;

  // Poll for generation status
  const {data: taskStatus} = useQuery({
    queryKey: ['workstream', 'health_score', generateTaskId],
    enabled: isGenerating,
    refetchInterval: 5000,
    queryFn: () =>
      getService(
        AiWorkstreamHealthScoreService
      ).getWorkstreamHealthScoreStatusApiV1WorkstreamHealthScoreGenerateTaskIdGet(
        generateTaskId as string
      ),
  });

  // Fetch cached health score after generation is started
  const {healthScore, rawHealthScore} = useWorkstreamHealthScore({
    workstreamId,
    checksType,
    enabled: isGenerating,
  });

  useEffect(() => {
    if (!generateTaskId || !taskStatus?.status || taskStatus.status === 'processing') return;

    if (taskStatus.status === 'completed') {
      queryClient.invalidateQueries({
        queryKey: ['workstream', 'health_score', workstreamId, checksType],
      });
      void onComplete?.();
      if (healthScore?.task_id === generateTaskId || rawHealthScore?.task_id === generateTaskId) {
        setGenerateTaskId(undefined);
      }
      return;
    }

    setGenerateTaskId(undefined); // Clear task ID to stop polling when status is received
  }, [
    workstreamId,
    checksType,
    generateTaskId,
    healthScore?.task_id,
    rawHealthScore?.task_id,
    onComplete,
    taskStatus,
  ]);

  const generate = useCallback(async () => {
    try {
      const response = await generateApi();
      setGenerateTaskId(response.task_id);
    } catch {
      setGenerateTaskId(undefined);
    }
  }, [generateApi]);

  return {
    generateTaskId,
    generatedHealthScore: healthScore,
    isGenerating,
    generate,
  };
};

// Where the health score popover was opened from, so we can measure intent
// (deliberate hover that reveals the checks) per surface in analytics.
export type HealthScoreSurface =
  | 'workstream_sidebar'
  | 'workstream_update_status_modal'
  | 'outcomes_validation'
  | 'health_score_dashboard';

export const WorkstreamHealthScore = ({
  workstream,
  iconSize,
  source,
  showText,
  style,
  textSize = 'small',
  openRequestKey,
  headerInfo,
  showSkeletonWhileLoading = false,
}: {
  workstream: Workstream;
  showSkeletonWhileLoading?: boolean;
  iconSize: number;
  source: HealthScoreSurface;
  showText?: boolean;
  style?: React.CSSProperties;
  textSize?: TextProps['size'];
  openRequestKey?: string;
  // Info tooltip beside the popover's score (for surfaces with no "Readiness" label).
  headerInfo?: string;
}) => {
  const checksType = 'default';
  const enabledGrowthbookFeatureIds = useEnabledGrowthbookFeatureIds();
  const {healthScore, isLoading} = useWorkstreamHealthScore({
    workstreamId: workstream.id,
    checksType,
  });

  const pendingBroadcastTask = usePendingWorkstreamHealthScoreTask({
    workstreamId: workstream.id,
    checksType,
  });
  const {generateTaskId, generatedHealthScore, generate} = useWorkstreamHealthScoreGenerate({
    workstreamId: workstream.id,
    checksType,
    timeframe: undefined,
  });

  const hasGeneratedRef = useRef<number | null>(null);
  useEffect(() => {
    // Don't generate if workstream already has health score cached
    if (isLoading || healthScore) return;

    // Only generate once per workstream
    if (hasGeneratedRef.current === workstream.id) {
      return;
    }

    hasGeneratedRef.current = workstream.id;
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstream.id, isLoading, healthScore]);

  const handleRegenerate = useCallback(() => {
    generate();
  }, [generate]);

  const healthScoreToDisplay = healthScore || generatedHealthScore;
  const rationale = healthScoreToDisplay?.rationale;
  const pendingBroadcastTaskId = pendingBroadcastTask?.taskId;
  // Local-only refreshes update deterministic checks while preserving saved AI
  // results, so keep rendering the saved server score until the refresh finishes.
  const shouldDisplaySavedAIChecksDuringLocalRefresh =
    Boolean(pendingBroadcastTask?.skipAIChecks) && !!healthScoreToDisplay;
  const pendingTaskId = generateTaskId ?? pendingBroadcastTaskId;
  const isHealthScoreStale = !!pendingTaskId && healthScoreToDisplay?.task_id !== pendingTaskId;
  const shouldUseServerHealthScore =
    !!healthScoreToDisplay && (shouldDisplaySavedAIChecksDuringLocalRefresh || !isHealthScoreStale);
  const allChecks = useMemo(() => {
    if (!shouldUseServerHealthScore) {
      return getLocalHealthScoreChecksToDisplay(workstream, enabledGrowthbookFeatureIds);
    }

    return getServerHealthScoreChecksToDisplay(
      workstream,
      healthScoreToDisplay?.checks ?? [],
      enabledGrowthbookFeatureIds
    );
  }, [
    workstream,
    healthScoreToDisplay?.checks,
    enabledGrowthbookFeatureIds,
    shouldUseServerHealthScore,
  ]);
  const [, checks] = getHealthScoreCriteria(
    workstream,
    allChecks,
    enabledGrowthbookFeatureIds,
    shouldUseServerHealthScore ? healthScoreToDisplay?.criteria_check_ids : undefined
  );
  const score = calculateHealthScore(checks);

  useEffect(() => {
    // Remove pending task ID when health score is finished generating
    if (pendingBroadcastTaskId && healthScore?.task_id === pendingBroadcastTaskId) {
      queryClient.setQueryData(
        getWorkstreamHealthScorePendingQueryKey(workstream.id, checksType),
        null
      );
    }
  }, [checksType, healthScore?.task_id, pendingBroadcastTaskId, workstream.id]);

  if (isLoading) {
    // The slot is known ahead of the score (health_score_enabled), so hold its
    // place rather than collapsing and popping in.
    return showSkeletonWhileLoading ? (
      // Mirrors HealthScoreResults' trigger (inline-flex, so it sits on a line box
      // the same height) so nothing shifts when the score lands. Skeleton widths
      // are max-widths; fixed-width wrappers keep them from collapsing.
      <Box display="inline-flex" alignItems="center" style={{gap: 8}}>
        <Box style={{width: iconSize, flexShrink: 0}}>
          <Skeleton.Box style={{height: iconSize}} />
        </Box>
        {showText ? (
          <Box style={{width: SKELETON_TEXT_WIDTH_PX, flexShrink: 0}}>
            <Skeleton.Text size={textSize === 'large' ? 'large' : 'small'} />
          </Box>
        ) : null}
      </Box>
    ) : null;
  }

  return (
    checks.length > 0 && (
      <HealthScoreResults
        workstreamId={workstream.id}
        score={score}
        rationale={shouldUseServerHealthScore ? rationale : undefined}
        checks={checks}
        onRegenerate={handleRegenerate}
        iconSize={iconSize}
        showText={showText}
        textSize={textSize}
        isRefreshing={isHealthScoreStale && !shouldDisplaySavedAIChecksDuringLocalRefresh}
        style={style}
        source={source}
        openRequestKey={openRequestKey}
        showFailingChecksFirst
        headerInfo={headerInfo}
      />
    )
  );
};

export const isWorkstreamCheckRefreshing = ({
  check,
  checksType,
  showRefreshingIcon,
}: {
  check: WorkstreamCheckResponseWithSource;
  checksType: WorkstreamCheckType;
  showRefreshingIcon: boolean | undefined;
}) => {
  const isValidCheck = getWorkstreamCheckDescription(check.id);
  if (!showRefreshingIcon || !isValidCheck) {
    return false;
  }

  const checkSource = getWorkstreamCheckSource(check.id, checksType);
  const isLocalPrecheckResponse =
    checkSource === 'local_precheck_and_ai' && check.resultSource === 'local';
  const canShowRefreshingIcon =
    checkSource === 'ai_only' || checkSource === 'local_precheck_and_ai';

  return Boolean(check.isAIPending) || (canShowRefreshingIcon && !isLocalPrecheckResponse);
};

const hasRefreshingWorkstreamChecks = ({
  checks,
  checksType,
  showRefreshingIcon,
}: {
  checks: WorkstreamCheckResponseWithSource[];
  checksType: WorkstreamCheckType;
  showRefreshingIcon: boolean | undefined;
}) =>
  checks.some((check) =>
    isWorkstreamCheckRefreshing({
      check,
      checksType,
      showRefreshingIcon,
    })
  );

export const HealthScoreResults = ({
  workstreamId,
  score,
  rationale,
  checks,
  source,
  iconSize,
  showText,
  textSize = 'small',
  textOverride,
  isRefreshing,
  style,
  contentWidth = '400px',
  showCheckRationale = false,
  openRequestKey,
  showFailingChecksFirst = false,
  headerInfo,
}: {
  workstreamId: number;
  score: string;
  rationale?: string | null;
  checks: WorkstreamCheckResponseWithSource[];
  source: HealthScoreSurface;
  onRegenerate?: () => void;
  iconSize?: number;
  showText?: boolean;
  textSize?: TextProps['size'];
  textOverride?: string;
  isRefreshing?: boolean;
  style?: React.CSSProperties;
  contentWidth?: React.CSSProperties['width'];
  showCheckRationale?: boolean;
  openRequestKey?: string;
  showFailingChecksFirst?: boolean;
  headerInfo?: string;
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const lastOpenRequestKeyRef = useRef<string | null>(null);

  const passingChecks = checks.filter((check) => check.passed);
  const isCheckRefreshing = hasRefreshingWorkstreamChecks({
    checks,
    checksType: 'health_score',
    showRefreshingIcon: isRefreshing,
  });

  const logOpen = useCallback(
    (open: boolean) => {
      if (!source || !open) {
        return;
      }

      analyticsLogger().logEvent('WORKSTREAM_HEALTH_SCORE_OPENED', {
        source,
        workstreamId,
        score,
        passingChecks: passingChecks.length,
        totalChecks: checks.length,
      });
    },
    [source, workstreamId, score, passingChecks.length, checks.length]
  );

  const handleToggle = useCallback(
    (open: boolean) => {
      setPopoverOpen(open);
      logOpen(open);
    },
    [logOpen]
  );

  useEffect(() => {
    if (!openRequestKey) {
      return;
    }

    if (lastOpenRequestKeyRef.current === openRequestKey) {
      return;
    }

    lastOpenRequestKeyRef.current = openRequestKey;
    setPopoverOpen(true);
    logOpen(true);
  }, [logOpen, openRequestKey]);

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    const closePopover = () => setPopoverOpen(false);
    window.addEventListener('scroll', closePopover, true);

    return () => {
      window.removeEventListener('scroll', closePopover, true);
    };
  }, [popoverOpen]);

  return (
    <Popover
      triggerStrategy="hover"
      open={popoverOpen}
      onToggle={handleToggle}
      closeOnOutsideClick={false}
      placement="right-start"
      offsetDistance={showText ? 8 : 4}
    >
      <Popover.Trigger asChild>
        <Box
          display="inline-flex"
          alignItems="center"
          style={{gap: showText ? 8 : 0, ...style}}
          data-testid="health-score-results"
        >
          <HealthScoreIcon score={score} iconSize={iconSize} barCount={3} />
          {showText ? <Text size={textSize}>{getHealthScoreLabel(score)}</Text> : null}
        </Box>
      </Popover.Trigger>
      <Popover.Overlay>
        <Popover.Content className={styles.healthScorePopoverContent}>
          <Box style={{width: contentWidth}}>
            <Popover.Header>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingLeft="6"
                paddingRight="6"
                paddingTop="4"
                paddingBottom="12"
              >
                <Box display="flex" alignItems="center" style={{gap: 8}}>
                  <HealthScoreIcon
                    score={score}
                    barCount={3}
                    iconSize={24}
                    textSize="large"
                    isRefreshing={isCheckRefreshing}
                    textOverride={textOverride}
                    showText
                  />
                  {headerInfo ? (
                    <Tooltip title={headerInfo}>
                      <Box display="flex" alignItems="center" tabIndex={0} aria-label={headerInfo}>
                        <UIIcon
                          src={InfoLine}
                          size="small"
                          className={atoms({color: 'Text Subtle'})}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                  {/* {!isCheckRefreshing && onRegenerate && (
                    <UIIcon
                      src={SyncingLine}
                      size="small"
                      aria-label="Refresh health score"
                      className={atoms({color: 'Text Subtle'})}
                      onClick={onRegenerate}
                      style={{cursor: 'pointer'}}
                    />
                  )} */}
                </Box>
                {!isCheckRefreshing && checks.length > 0 && (
                  <Text size="small" color="subtle">
                    {`${passingChecks.length} of ${checks.length}`}
                  </Text>
                )}
              </Box>
            </Popover.Header>
            <Popover.Body>
              <Box
                borderBottom="Solid"
                borderColor="Border Subtle"
                marginBottom="16"
                style={{marginLeft: '-12px', marginRight: '-12px'}}
              />
              {checks.length > 0 ? (
                <Box style={{paddingLeft: '4px', paddingRight: '8px', paddingBottom: '2px'}}>
                  <Stack gap="8">
                    <HealthScoreChecks
                      entityId={workstreamId}
                      checks={checks}
                      showRefreshingIcon={isCheckRefreshing}
                      showCheckRationale={showCheckRationale}
                      showFailingChecksFirst={showFailingChecksFirst}
                    />
                  </Stack>
                </Box>
              ) : (
                rationale && (
                  <Box marginTop="8" marginBottom="8">
                    <Text size="small">{rationale}</Text>
                  </Box>
                )
              )}
            </Popover.Body>
          </Box>
        </Popover.Content>
      </Popover.Overlay>
    </Popover>
  );
};

const getHealthScoreLabel = (score: string) => {
  switch (score.toLowerCase()) {
    case 'exemplary':
      return t('health_readiness_exemplary_label').toString();
    case 'adequate':
    case 'poor':
      return t('health_readiness_needs_attention_label').toString();
  }
};

export const HealthScoreChecks = ({
  entityId,
  checks,
  checksType = 'health_score',
  iconGap = 8,
  useFilledIcon = false,
  showRefreshingIcon = false,
  showCheckRationale = false,
  showFailingChecksFirst = false,
  textSize = 'small',
}: {
  entityId: number;
  checks: WorkstreamCheckResponseWithSource[];
  checksType?: WorkstreamCheckType;
  iconGap?: number;
  useFilledIcon?: boolean;
  showRefreshingIcon?: boolean;
  showCheckRationale?: boolean;
  showFailingChecksFirst?: boolean;
  textSize?: TextProps['size'];
}) => {
  if (!checks?.length) {
    return null;
  }

  const sortedChecks = showFailingChecksFirst
    ? [...checks.filter((check) => !check.passed), ...checks.filter((check) => check.passed)]
    : checks;
  return sortedChecks.map((check) => {
    const description = getWorkstreamCheckDescription(check.id);
    const isPassing = check.passed && !check.isAIPending;
    const isCheckRefreshing = isWorkstreamCheckRefreshing({
      check,
      checksType,
      showRefreshingIcon,
    });
    const aiRationale =
      showCheckRationale && !isCheckRefreshing && check.resultSource !== 'local'
        ? check.rationale?.trim()
        : undefined;
    return (
      description && (
        <Box key={`health-score-check__${entityId}__${check.id}`}>
          <Box
            display="flex"
            alignItems="center"
            className={styles.checkTitle}
            style={{gap: iconGap}}
          >
            <Box className={styles.checkIcon}>
              {isCheckRefreshing ? (
                <UIIcon
                  src={SyncingLine}
                  size="small"
                  aria-label="Refreshing check"
                  className={styles.refreshingIcon}
                />
              ) : (
                <UIIcon
                  src={
                    useFilledIcon
                      ? isPassing
                        ? CheckmarkCircleFill
                        : FailFill
                      : isPassing
                        ? CheckmarkLine
                        : CloseLine
                  }
                  style={{
                    color: useFilledIcon
                      ? isPassing
                        ? 'var(--dig-color__success__base)'
                        : 'var(--dig-color__alert__base--state-1)'
                      : 'var(--dig-color__text__subtle)',
                  }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="flex-start" style={{gap: 4}}>
              <Text size={textSize} isBold color={isPassing ? 'subtle' : undefined}>
                {description}
              </Text>
              {aiRationale && (
                <Tooltip title={aiRationale} placement="top">
                  <Box>
                    {/* eslint-disable-next-line deprecation/deprecation */}
                    <UIIcon src={AiLine} size="small" aria-label="AI rationale" />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
          {!isPassing && !isCheckRefreshing && check.failureCopy && (
            <Box style={{lineHeight: '16px', marginLeft: `${iconGap + 24}px`}}>
              <Text size={textSize} color="subtle">
                {check.failureCopy}
              </Text>
            </Box>
          )}
        </Box>
      )
    );
  });
};

export const HealthScoreIcon = ({
  score,
  isDisabled = false,
  gap = 6,
  barCount = 3,
  iconSize = 24,
  textSize = 'medium',
  isBold = true,
  isRefreshing = false,
  textOverride,
  tooltipText,
  showText,
  style,
}: {
  score?: string;
  isDisabled?: boolean;
  gap?: number;
  barCount?: 3 | 4;
  iconSize?: number;
  textSize?: TextProps['size'];
  isBold?: boolean;
  isRefreshing?: boolean;
  textOverride?: string;
  tooltipText?: string;
  showText?: boolean;
  style?: React.CSSProperties;
}) => {
  return (
    <Box display="flex" alignItems="center" style={{gap: gap ?? 6, ...style}}>
      {isRefreshing ? (
        <Box marginLeft="4" marginRight="4">
          <UIIcon
            src={SyncingLine}
            size="small"
            aria-label="Refreshing check"
            className={styles.refreshingIcon}
          />
        </Box>
      ) : (
        score && (
          <WorkstreamHealthScoreIcon
            score={score}
            iconSize={iconSize}
            barCount={barCount}
            isDisabled={isDisabled}
          />
        )
      )}
      {showText && (
        <>
          <Text size={textSize} isBold={isBold}>
            {isRefreshing
              ? t('health_readiness_refreshing_label').toString()
              : textOverride
                ? textOverride
                : score && getHealthScoreLabel(score)}
          </Text>
          {!isRefreshing && tooltipText && (
            <Tooltip title={tooltipText} placement="top">
              <Box style={{height: 16}}>
                <UIIcon src={InfoLine} size="small" />
              </Box>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};

const WorkstreamHealthScoreIcon = ({
  score,
  barCount,
  iconSize,
  isDisabled,
}: {
  score: string;
  barCount: 3 | 4;
  iconSize: number;
  isDisabled: boolean;
}) => {
  if (isDisabled) {
    return (
      <HealthScoreBarsSvgIcon
        width={iconSize}
        height={iconSize}
        bars={getDisabledHealthScoreBars(score, barCount)}
      />
    );
  }

  switch (score.toLowerCase()) {
    case 'exemplary':
      return <ExemplarySvgIcon width={iconSize} height={iconSize} barCount={barCount} />;
    case 'adequate':
      return <AdequateSvgIcon width={iconSize} height={iconSize} barCount={barCount} />;
    case 'poor':
      return <PoorSvgIcon width={iconSize} height={iconSize} barCount={barCount} />;
  }
  return null;
};

type HealthScoreBarConfig = {
  fill: string;
  opacity?: number;
};

const HealthScoreBarsSvgIcon = ({
  width,
  height,
  bars,
}: {
  width: number;
  height: number;
  bars: HealthScoreBarConfig[];
}) => {
  const barWidth = Math.round(width * 0.16);
  const gap = Math.round(width * 0.1);
  const sideInset = Math.round((width - barWidth * bars.length - gap * (bars.length - 1)) / 2);
  const baseline = height - Math.round(height * 0.125);
  const radius = Math.round(Math.min(width, height) * 0.05);
  const heightRatios = bars.length === 3 ? [0.25, 0.45, 0.7] : [0.25, 0.4, 0.55, 0.7];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      shapeRendering="crispEdges"
      xmlns="http://www.w3.org/2000/svg"
    >
      {bars.map((bar, index) => {
        const barHeight = Math.round(height * heightRatios[index]);
        return (
          <rect
            key={index}
            x={sideInset + index * (barWidth + gap)}
            y={baseline - barHeight}
            width={barWidth}
            height={barHeight}
            rx={radius}
            fill={bar.fill}
            opacity={bar.opacity}
          />
        );
      })}
    </svg>
  );
};

const getMutedHealthScoreBars = (count: number, fill: string, opacity?: number) =>
  Array.from({length: count}, () => ({fill, opacity}));

const useMutedHealthScoreBarOpacity = () =>
  useAtomValue(derivedThemeAtom) === 'dark' ? DARK_MODE_MUTED_BAR_OPACITY : undefined;

const getDisabledHealthScoreBars = (score: string, barCount: 3 | 4): HealthScoreBarConfig[] => {
  const disabledColor = 'var(--dig-color__disabled__surface)';
  const subtleColor = 'var(--dig-color__text__subtle)';
  const normalizedScore = score.toLowerCase();
  const activeBarCount =
    normalizedScore === 'exemplary'
      ? Math.min(3, barCount)
      : normalizedScore === 'adequate'
        ? Math.min(2, barCount)
        : normalizedScore === 'poor'
          ? 1
          : 0;

  return Array.from({length: barCount}, (_, index) => ({
    fill: index < activeBarCount ? subtleColor : disabledColor,
  }));
};

// Renders three green bars when no criteria are missing.
const ExemplarySvgIcon = ({
  width,
  height,
  barCount,
}: {
  width: number;
  height: number;
  barCount: 3 | 4;
}) => {
  return (
    <HealthScoreBarsSvgIcon
      width={width}
      height={height}
      bars={[
        ...getMutedHealthScoreBars(Math.min(3, barCount), HEALTH_SCORE_COMPLETE_COLOR),
        ...getMutedHealthScoreBars(Math.max(barCount - 3, 0), '#CFE0DA'),
      ]}
    />
  );
};

// Renders two pink bars when one or two criteria are missing.
export const AdequateSvgIcon = ({
  width,
  height,
  barCount,
}: {
  width: number;
  height: number;
  barCount: 3 | 4;
}) => {
  const mutedBarOpacity = useMutedHealthScoreBarOpacity();
  return (
    <HealthScoreBarsSvgIcon
      width={width}
      height={height}
      bars={[
        ...getMutedHealthScoreBars(Math.min(2, barCount), HEALTH_SCORE_CRITICAL_COLOR),
        ...getMutedHealthScoreBars(Math.max(barCount - 2, 0), '#F4C3D2', mutedBarOpacity),
      ]}
    />
  );
};

// Renders one pink bar when more than two criteria are missing.
const PoorSvgIcon = ({
  width,
  height,
  barCount,
}: {
  width: number;
  height: number;
  barCount: 3 | 4;
}) => {
  const mutedBarOpacity = useMutedHealthScoreBarOpacity();
  return (
    <HealthScoreBarsSvgIcon
      width={width}
      height={height}
      bars={[
        {fill: HEALTH_SCORE_CRITICAL_COLOR},
        ...getMutedHealthScoreBars(Math.max(barCount - 1, 0), '#F4C3D2', mutedBarOpacity),
      ]}
    />
  );
};
